import {Button, Modal, ToggleButton, ToggleButtonGroup} from 'react-bootstrap'
import {createPortal} from 'react-dom'
import {KTSVG} from '../../../../template/helpers'
import {useEffect, useState} from 'react'
import clsx from 'clsx'
import {promptAPI} from '../../../../services/PromptService'
import {initialNewPrompt, PromptTemplate} from '../../core/_models'
import {useIntl} from 'react-intl'
import {FieldsItem} from './FieldsItem'

type PromptType = 'sales' | 'support' | 'quality_control' | 'other'

type UserPromptTemplate = Record<PromptType, string>

const USE_RPROMPT_TEMPLATE: UserPromptTemplate = {
  sales: `
Проанализируй транскрипцию звонка менеджера по продажам.\n
Определи ключевые признаки, которые помогут понять качество лида, интерес клиента, вероятность сделки, основные возражения, следующий шаг и итог звонка.\n
Результат должен быть удобен для последующей фильтрации звонков, построения таблицы и аналитики по менеджерам.

Извлекаемые поля:
- quality: Качество транскрибации
Допустимые значения: Достаточно, Частично, Недостаточно
  `.trim(),
  support: `
Проанализируй транскрипцию звонка службы поддержки.\n
Определи ключевые признаки, которые помогут понять проблему клиента, критичность обращения, эмоциональное состояние клиента, был ли вопрос решен, нужна ли эскалация, есть ли риск потери клиента и какие действия были обещаны.\n
Результат должен быть удобен для последующей фильтрации звонков, построения таблицы и аналитики качества поддержки.

Извлекаемые поля:
- quality: Качество транскрибации
Допустимые значения: Достаточно, Частично, Недостаточно
  `.trim(),
  quality_control: `
Проанализируй транскрипцию звонка для контроля качества работы оператора.\n
Определи ключевые признаки, которые помогут оценить соблюдение скрипта, вежливость, полноту ответа, выявление потребности клиента, обработку возражений, наличие ошибок, итоговую оценку качества и рекомендации по улучшению.\n
Результат должен быть удобен для последующей фильтрации звонков, построения таблицы и оценки работы сотрудников.

Извлекаемые поля:
- quality: Качество транскрибации
Допустимые значения: Достаточно, Частично, Недостаточно
  `.trim(),
  other: `
Проанализируй транскрипцию звонка по пользовательской бизнес-задаче.\n
Определи ключевые признаки, которые помогут структурировать содержание звонка, выделить важные события, классифицировать результат, оценить риски, определить следующие действия и сформировать краткое резюме.\n
Результат должен быть удобен для последующей фильтрации звонков, построения таблицы и аналитики.
  `.trim(),
}

const modalsRoot = document.getElementById('root-modals') || document.body
export function ModalPromt({app_id, show, handleClose}: {app_id: string; show: boolean; handleClose: () => void}) {
  const intl = useIntl()
  const [getData, setGetData] = useState<boolean>(false)
  const {data: prompts} = promptAPI.useGetPromptsQuery(app_id, {skip: !getData})
  const [createPrompt] = promptAPI.useCreatePromptMutation()
  const [updtePrompt, {isLoading: updateIsLoading}] = promptAPI.useUpdatePromptMutation()
  const [generatePrompt, {isLoading: isLoadingGenerate}] = promptAPI.useGeneratePromptMutation()
  const [selectedNewPrompt, setSelectNewPrompt] = useState<PromptTemplate>(initialNewPrompt)
  const [selectedPrompt, setSelectedPrompt] = useState<PromptTemplate | null>(null)
  const [proccesUpdate, setProccesUpdate] = useState<PromptTemplate | null>(null)

  useEffect(() => {
    if (app_id === undefined) return
    if (show) {
      setGetData(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show])

  useEffect(() => {
    if (prompts) {
      if (proccesUpdate) {
        setSelectedPrompt(proccesUpdate)
        setProccesUpdate(null)
      } else {
        const activePrompt = prompts.find((pr) => pr.is_active)
        if (activePrompt) {
          setSelectedPrompt(activePrompt)
        }
      }
    }
    if (prompts && prompts.length === 0) {
      setSelectNewPrompt({...selectedNewPrompt, is_main: true})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prompts])

  const handlePromptCreate = () => {
    createPrompt({
      app_id: app_id,
      prompt: prompts?.length === 0 ? {...selectedNewPrompt, is_main: true} : selectedNewPrompt,
    })
    setSelectNewPrompt(initialNewPrompt)
  }

  const handlePromptUpdate = () => {
    if (selectedPrompt) {
      updtePrompt({app_id: app_id, data: selectedPrompt})
      setProccesUpdate(selectedPrompt)
    }
  }

  const handleIsMainChecked = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedPrompt) return
    setSelectedPrompt({...selectedPrompt, is_main: e.target.checked})
  }

  const handleGeneratePrompt = (text: string) => {
    generatePrompt({app_id: app_id, data: {text: text || USE_RPROMPT_TEMPLATE.quality_control}})
      .unwrap()
      .then((response) => {
        if (response === null) return

        if (selectedPrompt === null) {
          setSelectNewPrompt((prev) => ({
            ...prev,
            name: prev.name === '' ? response.template_name || '' : prev.name,
            description: prev.description === '' ? response.template_description || '' : prev.description,
            text: prev.text === '' ? response.user_raw_promt || '' : prev.text,
            spec: response,
          }))
        } else {
          setSelectedPrompt({...selectedPrompt, spec: response})
        }
      })
  }

  const handleClear = () => {
    setSelectNewPrompt(initialNewPrompt)
    setSelectedPrompt(null)
    setProccesUpdate(null)
    handleClose()
  }

  const handleClearWithoutClose = () => {
    setSelectNewPrompt(initialNewPrompt)
    setSelectedPrompt(null)
    setProccesUpdate(null)
  }

  const handleFilterableChange = (key: string, filterable: boolean, mode: 'new' | 'edit') => {
    const updateFields = (fields: any[]) => fields.map((f: any) => (f.key === key ? {...f, filterable} : f))

    if (mode === 'new') {
      setSelectNewPrompt((prev) => ({
        ...prev,
        spec: prev.spec ? {...prev.spec, fields: updateFields(prev.spec.fields || [])} : prev.spec,
      }))
    } else {
      setSelectedPrompt((prev) => {
        if (!prev) return prev
        return {
          ...prev,
          spec: prev.spec ? {...prev.spec, fields: updateFields(prev.spec.fields || [])} : prev.spec,
        }
      })
    }
  }

  return createPortal(
    <Modal show={show} onHide={handleClear} centered size='xl'>
      <Modal.Header className='py-3'>
        <Modal.Title>
          <i className='bi bi-filetype-ai fs-4 me-3 text-info fw-bold' />
          Управление промтами
          <div>
            <span className='text-muted mt-1 fw-semibold fs-7'>Список, добавление и редактирование промтов</span>
          </div>
        </Modal.Title>
        <span className='cursor-pointer' onClick={handleClear}>
          <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-1' />
        </span>
      </Modal.Header>
      <Modal.Body className='d-flex flex-column py-3 px-5' style={{height: 'calc(100vh - 160px)', overflow: 'hidden'}}>
        <div className='d-flex flex-column align-items-stretch flex-grow-1' style={{minHeight: 0}}>
          <div className='h-100 d-flex flex-column overflow-hidden'>
            <div className='d-flex gap-3 overflow-hidden flex-grow-1' style={{minHeight: 0}}>
              <div className='flex-row-fluid overflow-auto' style={{minHeight: 0}}>
                <div className='h-100'>
                  <Button size='sm' className='w-100 mb-3' onClick={handleClearWithoutClose}>
                    Добавить промт
                  </Button>
                  {prompts?.length === 0 && (
                    <div className='d-flex justify-content-center mt-10'>
                      <i className='bi bi-inbox text-gray-300 fs-4x'></i>
                    </div>
                  )}
                  {prompts?.map((prompt) => (
                    <div
                      key={prompt.id}
                      className='d-flex align-items-center gap-1 mb-2 rounded px-2'
                      style={{backgroundColor: 'rgba(var(--kt-primary-rgb), 0.16)'}}
                    >
                      <button
                        className='btn btn-sm flex-grow-1 d-flex align-items-center text-start p-0'
                        style={{minWidth: 0}}
                        type='button'
                        onClick={() => setSelectedPrompt(prompt)}
                      >
                        <span className='d-block overflow-hidden p-3' style={{minWidth: 0}}>
                          <span className='d-block text-truncate'>{prompt.name}</span>
                          <span className='d-block text-muted fs-8'>{prompt.description}</span>
                          {prompt.is_main && (
                            <span className='badge badge-light-info mt-2 me-3' style={{marginLeft: '-5px'}}>
                              по умолчанию
                            </span>
                          )}
                          {prompt.is_active ? (
                            <span className='badge badge-light-success mt-2' style={{marginLeft: '-5px'}}>
                              активен
                            </span>
                          ) : (
                            <span className='badge badge-light-danger mt-2' style={{marginLeft: '-5px'}}>
                              неактивен
                            </span>
                          )}
                        </span>
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {selectedPrompt === null ? (
                <div className='flex-row-auto overflow-auto' style={{width: '70%', minHeight: 0}}>
                  <div className='p-3'>
                    <span className='small text-uppercase text-muted'>Новый промт</span>
                    <div className='card-header mt-3'>
                      <div className='card-title m-0'>
                        <h3 className='fw-bolder m-0'>{selectedNewPrompt.name}</h3>
                        <span className='d-block text-muted fs-8'>{selectedNewPrompt.description}</span>
                      </div>
                    </div>
                    <div className='card-body pt-6 pb-5'>
                      <div className='mb-4 px-0'>
                        <label className='col-form-label text-muted required py-2'>
                          <span className='small'>Название промта</span>
                        </label>
                        <input
                          type='text'
                          className='form-control form-control-sm text-gray-900'
                          value={selectedNewPrompt.name ?? ''}
                          onChange={(e) => setSelectNewPrompt({...selectedNewPrompt, name: e.target.value})}
                        />
                      </div>
                      <div className='mb-4 px-0'>
                        <label className='col-form-label text-muted py-2'>
                          <span className='small'>Описание</span>
                        </label>
                        <input
                          className='form-control text-gray-900 fs-7'
                          id='description'
                          value={selectedNewPrompt.description ?? ''}
                          onChange={(e) => setSelectNewPrompt({...selectedNewPrompt, description: e.target.value})}
                        />
                      </div>
                      <div className='mb-4 px-0'>
                        <label className='col-form-label text-muted py-2 required'>
                          <span className='small'>Текст промта</span>
                        </label>
                        <textarea
                          className='form-control text-gray-900 fs-7'
                          rows={10}
                          id='promt'
                          value={selectedNewPrompt.text ?? ''}
                          onChange={(e) => setSelectNewPrompt({...selectedNewPrompt, text: e.target.value})}
                        />
                        <div className='d-flex justify-content-between mt-3 mb-4'>
                          <div className=''>
                            <span className='small text-muted'>Цели анализа (базовые шаблоны): </span>
                            <span
                              className='badge badge-light-info cursor-pointer me-2'
                              onClick={() =>
                                setSelectNewPrompt({...selectedNewPrompt, text: USE_RPROMPT_TEMPLATE.sales.trim()})
                              }
                            >
                              Продажи
                            </span>
                            <span
                              className='badge badge-light-info cursor-pointer me-2'
                              onClick={() =>
                                setSelectNewPrompt({...selectedNewPrompt, text: USE_RPROMPT_TEMPLATE.support.trim()})
                              }
                            >
                              Поддержка
                            </span>
                            <span
                              className='badge badge-light-info cursor-pointer me-2'
                              onClick={() =>
                                setSelectNewPrompt({
                                  ...selectedNewPrompt,
                                  text: USE_RPROMPT_TEMPLATE.quality_control.trim(),
                                })
                              }
                            >
                              Контроль качества
                            </span>
                            <span
                              className='badge badge-light-info cursor-pointer me-2'
                              onClick={() =>
                                setSelectNewPrompt({...selectedNewPrompt, text: USE_RPROMPT_TEMPLATE.other.trim()})
                              }
                            >
                              Другое
                            </span>
                          </div>
                          <div className='mb-4'>
                            <span
                              className='badge badge-light-success cursor-pointer me-2'
                              onClick={() => handleGeneratePrompt(selectedNewPrompt.text!)}
                            >
                              {isLoadingGenerate ? (
                                <>
                                  <span className='indicator-progress' style={{display: 'block'}}>
                                    <span className='spinner-border spinner-border-sm align-middle me-2' />
                                  </span>
                                  <span>Получение полей</span>
                                </>
                              ) : (
                                <span>Получить поля</span>
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                      {/* <div className='alert alert-warning d-flex align-items-center p-5 mb-3'>
                        <KTSVG
                          path='/media/icons/duotune/general/gen045.svg'
                          className='svg-icon-warning svg-icon-2hx me-3'
                        />
                        <span className='text-muted'>
                          После сохранения текста промта система автоматически извлечёт поля.
                        </span>
                      </div> */}
                      <div className='mb-4 pt-3 px-1 border border-1 rounded'>
                        <div className='accordion accordion-icon-toggle' id='kt_accordion_1'>
                          <div className='mb-2'>
                            <div
                              className='accordion-header d-flex'
                              data-bs-toggle='collapse'
                              data-bs-target='#kt_accordion_1_item_1'
                            >
                              <span className='accordion-icon'>
                                <KTSVG
                                  path='/media/icons/duotune/arrows/arr064.svg'
                                  className='svg-icon svg-icon-3 me-3'
                                />
                              </span>
                              <label className='col-form-label py-0 mb-2 cursor-pointer'>
                                <span className={clsx('small', {'text-muted': !selectedNewPrompt.is_active})}>
                                  Использование полей в фильтрах журнала звонков
                                </span>
                                <span className='d-block text-muted fs-8'>
                                  Выберете пункты, которые будут отображаться в фильтрах журнала звонков, для
                                  отображения определенных вызовов
                                </span>
                              </label>
                            </div>
                            <div
                              id='kt_accordion_1_item_1'
                              className='fs-6 collapse show ps-10'
                              data-bs-parent='#kt_accordion_1'
                            >
                              {selectedNewPrompt.spec &&
                                selectedNewPrompt.spec.fields!.map((f: any) => (
                                  <FieldsItem
                                    key={f.key}
                                    field={f}
                                    onChange={(key, filterable) => handleFilterableChange(key, filterable, 'new')}
                                  />
                                ))}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className='separator mt-7 mb-4' />
                      <div className='mb-4 px-0'>
                        <label className='col-form-label text-muted py-2'>
                          <span className='small'>Статус</span>
                        </label>
                        <div>
                          <ToggleButtonGroup
                            type='radio'
                            name='options'
                            value={selectedNewPrompt.is_active ? 1 : 0}
                            onChange={(val) =>
                              setSelectNewPrompt({...selectedNewPrompt, is_active: val === 1 ? true : false})
                            }
                          >
                            <ToggleButton
                              id='promt-radio-active'
                              value={1}
                              variant={selectedNewPrompt.is_active ? 'primary' : 'light-primary'}
                              size='sm'
                              className='d-flex align-items-center h-30px'
                            >
                              Активен
                            </ToggleButton>

                            <ToggleButton
                              id='promt-radio-inactive'
                              value={0}
                              variant={!selectedNewPrompt.is_active ? 'primary' : 'light-primary'}
                              size='sm'
                              className='d-flex align-items-center h-30px'
                              disabled={(prompts && prompts.length === 0) || selectedNewPrompt.is_main}
                            >
                              Неактивен
                            </ToggleButton>
                          </ToggleButtonGroup>
                        </div>
                      </div>
                      <div className='mb-4 p-3 border border-1 rounded'>
                        {/* <label className='col-form-label py-0 mb-2'>
                          <div className='d-flex justify-content-between'>
                            <span className={clsx('small', {'text-muted': !selectedNewPrompt.is_active})}>
                              Когда применять промт
                            </span>
                            <span className='badge badge-light-info small'>условие: ИЛИ</span>
                          </div>
                          <span className='d-block text-muted fs-8'>
                            Правила срабатывают только для активного промта. Если включено несколько условий, достаточно
                            любого совпадения.
                          </span>
                        </label> */}
                        {/* <div className='mb-4 border border-1 rounded p-3'>
                          <div className='form-check form-check-custom form-check-solid mb-3'>
                            <input
                              className='form-check-input'
                              type='checkbox'
                              id='сheckLines'
                              disabled={!selectedNewPrompt.is_active}
                            />
                            <label className='form-check-label' htmlFor='сheckLines'>
                              Вызов поступил на любую из входящих линий
                            </label>
                          </div>
                          <input
                            type='text'
                            className='form-control form-control-sm text-gray-900'
                            placeholder='линии через запятую, например: +375292221133, +375293331122'
                            disabled={!selectedNewPrompt.is_active}
                          />
                        </div> */}
                        {/* <div className='mb-4 border border-1 rounded p-3'>
                          <div className='form-check form-check-custom form-check-solid mb-3'>
                            <input
                              className='form-check-input'
                              type='checkbox'
                              id='checkExtensions'
                              disabled={!selectedNewPrompt.is_active}
                            />
                            <label className='form-check-label' htmlFor='checkExtensions'>
                              Разговор был с любым из сотрудников
                            </label>
                          </div>
                          <input
                            type='text'
                            className='form-control form-control-sm text-gray-900'
                            placeholder='сотрудники через запятую, например: 203, 301'
                            disabled={!selectedNewPrompt.is_active}
                          />
                        </div> */}
                        <div className='mb-4 border border-1 rounded p-3'>
                          <div className='form-check form-check-custom form-check-solid'>
                            <input
                              className='form-check-input'
                              type='checkbox'
                              id='checkDefault'
                              checked={!!selectedNewPrompt.is_main}
                              disabled={!selectedNewPrompt.is_active || (prompts && prompts.length === 0)}
                              onChange={(e) => setSelectNewPrompt({...selectedNewPrompt, is_main: e.target.checked})}
                            />
                            <label className='form-check-label' htmlFor='checkDefault'>
                              Промт по умолчанию
                            </label>
                          </div>
                        </div>
                      </div>
                      <div className='d-flex justify-content-end p-2'>
                        <button
                          type='submit'
                          className='btn btn-sm btn-primary me-1'
                          onClick={handlePromptCreate}
                          disabled={selectedNewPrompt.name === '' || selectedNewPrompt.text === ''}
                        >
                          {intl.formatMessage({id: 'BUTTON.SAVE'})}
                        </button>
                        <button
                          className='btn btn-sm btn-light-primary align-self-center'
                          onClick={() => {
                            setSelectNewPrompt(initialNewPrompt)
                            const activePrompt = prompts!.find((pr) => pr.is_active)
                            if (activePrompt !== undefined) {
                              setSelectedPrompt(activePrompt!)
                            } else {
                              handleClear()
                            }
                          }}
                        >
                          {intl.formatMessage({id: 'BUTTON.CANCEL'})}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className='flex-row-auto overflow-auto' style={{width: '70%', minHeight: 0}}>
                  <div className='p-3'>
                    <span className='small text-uppercase text-muted'>Редактирование</span>
                    <div className='card-header mt-3'>
                      <div className='card-title m-0'>
                        <div className='d-flex justify-content-between'>
                          <div>
                            <h3 className='fw-bolder m-0'>{selectedPrompt.name}</h3>
                          </div>
                          {selectedPrompt.is_main && <span className='badge badge-light-info'>по умолчанию</span>}
                        </div>
                        <span className='d-block text-muted fs-8'>{selectedPrompt.description}</span>
                      </div>
                    </div>
                    <div className='card-body pt-6 pb-5'>
                      <div className='mb-4 px-0'>
                        <label className='col-form-label text-muted required py-2'>
                          <span className='small'>Название промта</span>
                        </label>
                        <input
                          type='text'
                          className='form-control form-control-sm text-gray-900'
                          value={selectedPrompt.name ?? ''}
                          onChange={(e) => setSelectedPrompt({...selectedPrompt, name: e.target.value})}
                        />
                      </div>
                      <div className='mb-4 px-0'>
                        <label className='col-form-label text-muted py-2'>
                          <span className='small'>Описание</span>
                        </label>
                        <input
                          className='form-control text-gray-900 fs-7'
                          id='description'
                          value={selectedPrompt.description ?? ''}
                          onChange={(e) => setSelectedPrompt({...selectedPrompt, description: e.target.value})}
                        />
                      </div>
                      <div className='mb-4 px-0'>
                        <label className='col-form-label text-muted py-2 required'>
                          <span className='small'>Текст промта</span>
                        </label>
                        <textarea
                          className='form-control text-gray-900 fs-7'
                          rows={10}
                          id='promt'
                          value={selectedPrompt.text ?? ''}
                          onChange={(e) => setSelectedPrompt({...selectedPrompt, text: e.target.value})}
                        />
                        <div className='d-flex justify-content-between mt-3 mb-4'>
                          <div className=''>
                            <span className='small text-muted'>Цели анализа (базовые шаблоны): </span>
                            <span
                              className='badge badge-light-info cursor-pointer me-2'
                              onClick={() =>
                                setSelectedPrompt({...selectedPrompt, text: USE_RPROMPT_TEMPLATE.sales.trim()})
                              }
                            >
                              Продажи
                            </span>
                            <span
                              className='badge badge-light-info cursor-pointer me-2'
                              onClick={() =>
                                setSelectedPrompt({...selectedPrompt, text: USE_RPROMPT_TEMPLATE.support.trim()})
                              }
                            >
                              Поддержка
                            </span>
                            <span
                              className='badge badge-light-info cursor-pointer me-2'
                              onClick={() =>
                                setSelectedPrompt({
                                  ...selectedPrompt,
                                  text: USE_RPROMPT_TEMPLATE.quality_control.trim(),
                                })
                              }
                            >
                              Контроль качества
                            </span>
                            <span
                              className='badge badge-light-info cursor-pointer me-2'
                              onClick={() =>
                                setSelectedPrompt({...selectedPrompt, text: USE_RPROMPT_TEMPLATE.other.trim()})
                              }
                            >
                              Другое
                            </span>
                          </div>
                          <div className='mb-4 w-full text-center'>
                            <span
                              className='badge badge-light-success cursor-pointer me-2'
                              onClick={() => handleGeneratePrompt(selectedPrompt.text!)}
                            >
                              {isLoadingGenerate ? (
                                <>
                                  <span className='indicator-progress' style={{display: 'block'}}>
                                    <span className='spinner-border spinner-border-sm align-middle me-2' />
                                  </span>
                                  <span>Получение полей</span>
                                </>
                              ) : (
                                <span>Получить поля</span>
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                      {/* <div className='alert alert-warning d-flex align-items-center p-5 mb-3'>
                        <KTSVG
                          path='/media/icons/duotune/general/gen045.svg'
                          className='svg-icon-warning svg-icon-2hx me-3'
                        />
                        <span className='text-muted'>
                          После сохранения текста промта система автоматически извлечёт поля.
                        </span>
                      </div> */}
                      <div className='mb-4 pt-3 px-1 border border-1 rounded'>
                        <div className='accordion accordion-icon-toggle' id='kt_accordion_2'>
                          <div className='mb-2'>
                            <div
                              className='accordion-header d-flex'
                              data-bs-toggle='collapse'
                              data-bs-target='#kt_accordion_2_item_1'
                            >
                              <span className='accordion-icon'>
                                <KTSVG
                                  path='/media/icons/duotune/arrows/arr064.svg'
                                  className='svg-icon svg-icon-3 me-3'
                                />
                              </span>
                              <label className='col-form-label py-0 mb-2 cursor-pointer'>
                                <span className={clsx('small', {'text-muted': !selectedPrompt.is_active})}>
                                  Использование полей в фильтрах журнала звонков
                                </span>
                                <span className='d-block text-muted fs-8'>
                                  Выберете пункты, которые будут отображаться в фильтрах журнала звонков, для
                                  отображения определенных вызовов
                                </span>
                              </label>
                            </div>
                            <div
                              id='kt_accordion_2_item_1'
                              className='fs-6 collapse show ps-10'
                              data-bs-parent='#kt_accordion_2'
                            >
                              {selectedPrompt.spec &&
                                selectedPrompt.spec.fields!.map((f: any) => (
                                  <FieldsItem
                                    key={f.key}
                                    field={f}
                                    onChange={(key, filterable) => handleFilterableChange(key, filterable, 'edit')}
                                  />
                                ))}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className='separator mt-7 mb-4' />
                      <div className='mb-4 px-0'>
                        <label className='col-form-label text-muted py-2'>
                          <span className='small'>Статус</span>
                          <span className='d-block text-muted fs-9'>Промт по умолчанию отключить нельзя</span>
                        </label>
                        <div>
                          <ToggleButtonGroup
                            type='radio'
                            name='options'
                            value={selectedPrompt.is_active ? 1 : 0}
                            onChange={(val) => {
                              setSelectedPrompt({...selectedPrompt, is_active: val === 1 ? true : false})
                            }}
                          >
                            <ToggleButton
                              id='promt-radio-active'
                              value={1}
                              variant={selectedPrompt.is_active ? 'primary' : 'light-primary'}
                              size='sm'
                              className='d-flex align-items-center h-30px'
                            >
                              Активен
                            </ToggleButton>
                            <ToggleButton
                              id='promt-radio-inactive'
                              value={0}
                              variant={!selectedPrompt.is_active ? 'primary' : 'light-primary'}
                              size='sm'
                              className='d-flex align-items-center h-30px'
                              disabled={selectedPrompt.is_main && prompts?.length !== 0}
                            >
                              Неактивен
                            </ToggleButton>
                          </ToggleButtonGroup>
                        </div>
                      </div>
                      <div className='mb-4 p-3 border border-1 rounded'>
                        {/* <label className='col-form-label py-0 mb-2'>
                          <div className='d-flex justify-content-between'>
                            <span className={clsx('small', {'text-muted': !selectedPrompt.is_active})}>
                              Когда применять промт
                            </span>
                            <span className='badge badge-light-info small'>условие: ИЛИ</span>
                          </div>
                          <span className='d-block text-muted fs-8'>
                            Правила срабатывают только для активного промта. Если включено несколько условий, достаточно
                            любого совпадения.
                          </span>
                        </label> */}
                        {/* <div className='mb-4 border border-1 rounded p-3'>
                          <div className='form-check form-check-custom form-check-solid mb-3'>
                            <input
                              className='form-check-input'
                              type='checkbox'
                              id='сheckLines'
                              disabled={!selectedPrompt.is_active}
                            />
                            <label className='form-check-label' htmlFor='сheckLines'>
                              Вызов поступил на любую из входящих линий
                            </label>
                          </div>
                          <input
                            type='text'
                            className='form-control form-control-sm text-gray-900'
                            placeholder='линии через запятую, например: +375292221133, +375293331122'
                            disabled={!selectedPrompt.is_active}
                          />
                        </div> */}
                        {/* <div className='mb-4 border border-1 rounded p-3'>
                          <div className='form-check form-check-custom form-check-solid mb-3'>
                            <input
                              className='form-check-input'
                              type='checkbox'
                              id='checkExtensions'
                              disabled={!selectedPrompt.is_active}
                            />
                            <label className='form-check-label' htmlFor='checkExtensions'>
                              Разговор был с любым из сотрудников
                            </label>
                          </div>
                          <input
                            type='text'
                            className='form-control form-control-sm text-gray-900'
                            placeholder='сотрудники через запятую, например: 203, 301'
                            disabled={!selectedPrompt.is_active}
                          />
                        </div> */}

                        <div className='mb-4 border border-1 rounded p-3'>
                          <div className='form-check form-check-custom form-check-solid'>
                            <input
                              className='form-check-input'
                              type='checkbox'
                              id='checkDefault'
                              checked={!!selectedPrompt.is_main}
                              disabled={
                                !selectedPrompt.is_active ||
                                !!prompts!.find((pr) => pr.id === selectedPrompt.id && pr.is_main)
                              }
                              onChange={handleIsMainChecked}
                            />
                            <label className='form-check-label' htmlFor='checkDefault'>
                              Промт по умолчанию
                            </label>
                          </div>
                        </div>
                      </div>
                      <div className='d-flex justify-content-end p-2'>
                        <button
                          type='submit'
                          className='btn btn-sm btn-primary me-3'
                          onClick={handlePromptUpdate}
                          disabled={updateIsLoading}
                        >
                          {!updateIsLoading && intl.formatMessage({id: 'BUTTON.SAVE'})}
                          {updateIsLoading && (
                            <span className='indicator-progress' style={{display: 'block'}}>
                              {intl.formatMessage({id: 'WAIT'})}
                              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                            </span>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>,
    modalsRoot
  )
}
